import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Check, AlertTriangle, Loader2, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { GumletPlayer } from "./Gumlet";
import { BunnyStreamPlayer } from "@/components/BunnyStream";
import { VimeoPlayer } from "@/components/Vimeo";

type VideoProvider = "gumlet" | "bunnystream" | "vimeo";

interface VideoIds {
  gumlet: string;
  bunnystream: string;
  vimeo: string;
}

interface VideoPlayerProps {
  videoIds: VideoIds;
  onPlaybackSuccess: () => void;
  onPlaybackFailure: () => void;
  isRetrying: boolean;
  preferredProvider?: VideoProvider;
}

interface ProviderStatus {
  provider: VideoProvider;
  status: "pending" | "testing" | "success" | "failure";
  error?: string;
}

// Add browser compatibility check
const checkBrowserCompatibility = () => {
  const forceIncompatible = false;

  if (forceIncompatible) {
    return {
      isCompatible: false,
      browser: "Test Browser",
    };
  }

  const ua = navigator.userAgent;
  const isIE = ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
  const isOldEdge =
    ua.indexOf("Edge/") > -1 && parseInt(ua.split("Edge/")[1]) < 79;

  const hasRequiredFeatures =
    typeof window.Promise !== "undefined" &&
    typeof window.fetch !== "undefined" &&
    typeof window.URL !== "undefined" &&
    typeof window.URLSearchParams !== "undefined" &&
    typeof window.HTMLVideoElement !== "undefined";

  return {
    isCompatible: !isIE && !isOldEdge && hasRequiredFeatures,
    browser: ua,
  };
};

const VideoPlayer = ({
  videoIds,
  onPlaybackSuccess,
  onPlaybackFailure,
  isRetrying,
  preferredProvider = "gumlet",
}: VideoPlayerProps) => {
  const [status, setStatus] = useState<"loading" | "success" | "failure">(
    "loading"
  );
  const [hasPlayed, setHasPlayed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playerKey, setPlayerKey] = useState(0);
  const [currentProvider, setCurrentProvider] =
    useState<VideoProvider>(preferredProvider);
  const [browserInfo, setBrowserInfo] = useState(checkBrowserCompatibility());
  const [providerStatuses, setProviderStatuses] = useState<ProviderStatus[]>([
    { provider: "gumlet", status: "pending" },
    { provider: "bunnystream", status: "pending" },
    { provider: "vimeo", status: "pending" },
  ]);
  const [isAutoTesting, setIsAutoTesting] = useState(false);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const checkTimeoutRef = useRef<number | null>(null);
  const autoTestTimeoutRef = useRef<number | null>(null);
  const successHandledRef = useRef(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef<number | null>(null);
  const isAutoTestingRef = useRef(false);
  const currentTestIndexRef = useRef(0);
  const currentProviderRef = useRef<VideoProvider>(preferredProvider);
  const [nextProviderName, setNextProviderName] = useState<string | null>(null);
  const transitionScreenTimeoutRef = useRef<number | null>(null);

  // Provider fallback order — matches the UI listing
  const providerFallbackOrder: VideoProvider[] = [
    "gumlet",
    "bunnystream",
    "vimeo",
  ];

  useEffect(() => {
    // Check browser compatibility on mount
    setBrowserInfo(checkBrowserCompatibility());
    // Start auto-test on mount
    startAutoTest();
  }, []);

  useEffect(() => {
    // On retry, reset status and force player reload
    if (isRetrying) {
      setStatus("loading");
      setHasPlayed(false);
      setProgress(0);
      setPlayerKey((prev) => prev + 1);
      // Reset to first provider in fallback order
      setCurrentProvider(providerFallbackOrder[0]);
      setProviderStatuses((prev) =>
        prev.map((p) => ({
          ...p,
          status:
            p.provider === providerFallbackOrder[0] ? "testing" : "pending",
          error: undefined,
        }))
      );
    }
  }, [isRetrying]);

  useEffect(() => {
    // Timeout to detect playback failure if nothing starts
    if (status === "loading") {
      checkTimeoutRef.current = window.setTimeout(() => {
        if (!hasPlayed) {
          handleProviderFailure();
        }
      }, 30000);
    }

    return () => {
      if (checkTimeoutRef.current) {
        window.clearTimeout(checkTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, hasPlayed]);

  useEffect(() => {
    // Handle auto-testing
    if (isAutoTesting) {
      const nextProvider = providerFallbackOrder[currentTestIndex];
      testSpecificProvider(nextProvider);
    }

    return () => {
      if (autoTestTimeoutRef.current) {
        window.clearTimeout(autoTestTimeoutRef.current);
      }
    };
  }, [isAutoTesting, currentTestIndex]);

  const startAutoTest = () => {
    setIsAutoTesting(true);
    isAutoTestingRef.current = true;
    setCurrentTestIndex(0);
    currentTestIndexRef.current = 0;
    setProviderStatuses((prev) =>
      prev.map((p) => ({
        ...p,
        status: "pending",
        error: undefined,
      }))
    );
  };

  const stopAutoTest = (checkResults = true) => {
    setIsAutoTesting(false);
    isAutoTestingRef.current = false;
    setNextProviderName(null);
    if (autoTestTimeoutRef.current) {
      window.clearTimeout(autoTestTimeoutRef.current);
    }
    if (transitionScreenTimeoutRef.current) {
      window.clearTimeout(transitionScreenTimeoutRef.current);
    }

    // After auto-test ends, check if ANY provider failed
    if (checkResults) {
      setProviderStatuses((prev) => {
        const anyFailed = prev.some((p) => p.status === "failure");
        const anySucceeded = prev.some((p) => p.status === "success");
        if (anyFailed) {
          // Even one failure means the user should troubleshoot
          setStatus(anySucceeded ? "success" : "failure");
          // Delay slightly so the user can see the final results
          setTimeout(() => onPlaybackFailure(), 1500);
        } else if (anySucceeded) {
          setStatus("success");
        }
        return prev;
      });
    }
  };

  const handleProviderFailure = () => {
    const provider = currentProviderRef.current;

    // Update status for current provider
    setProviderStatuses((prev) =>
      prev.map((p) =>
        p.provider === provider
          ? { ...p, status: "failure", error: "Playback failed to start" }
          : p
      )
    );

    if (isAutoTestingRef.current) {
      // Move to next provider in auto-test sequence
      if (currentTestIndexRef.current < providerFallbackOrder.length - 1) {
        advanceAutoTestWithDelay();
      } else {
        stopAutoTest();
      }
    } else {
      // Manual single-provider test failed
      setStatus("failure");
      onPlaybackFailure();
    }
  };

  const handleRetry = () => {
    setStatus("loading");
    setHasPlayed(false);
    setProgress(0);
    setPlayerKey((prev) => prev + 1);
    setCurrentProvider(preferredProvider);
    setProviderStatuses((prev) =>
      prev.map((p) => ({
        ...p,
        status: p.provider === preferredProvider ? "testing" : "pending",
        error: undefined,
      }))
    );
  };

  const handlePlaybackSuccess = () => {
    // Guard: ignore duplicate onPlay calls (ref is synchronous, not batched like state)
    if (successHandledRef.current) return;
    successHandledRef.current = true;

    // End crossfade transition — fade the new player in
    setIsTransitioning(false);

    const provider = currentProviderRef.current;
    setProviderStatuses((prev) =>
      prev.map((p) =>
        p.provider === provider ? { ...p, status: "success" } : p
      )
    );

    if (isAutoTestingRef.current) {
      // Let the player run for 5 seconds so users can see it working
      autoTestTimeoutRef.current = window.setTimeout(() => {
        if (currentTestIndexRef.current < providerFallbackOrder.length - 1) {
          advanceAutoTestWithDelay();
        } else {
          // stopAutoTest will check results and set status + trigger failure if needed
          stopAutoTest();
        }
      }, 5000);
    } else {
      setStatus("success");
      setHasPlayed(true);
      onPlaybackSuccess();
    }
  };

  const advanceAutoTestWithDelay = () => {
    const nextIndex = currentTestIndexRef.current + 1;
    const nextProvider = providerFallbackOrder[nextIndex];
    setNextProviderName(nextProvider);

    // Mark next provider as testing immediately so the status panel updates
    setProviderStatuses((prev) =>
      prev.map((p) => ({
        ...p,
        status: p.provider === nextProvider ? "testing" : p.status,
      }))
    );

    // Show transition screen for 3 seconds, then advance
    transitionScreenTimeoutRef.current = window.setTimeout(() => {
      setNextProviderName(null);
      currentTestIndexRef.current = nextIndex;
      setCurrentTestIndex(nextIndex);
    }, 3000);
  };

  const testSpecificProvider = (provider: VideoProvider) => {
    // Clear any pending transition timer
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }

    // Start fade-out
    setIsTransitioning(true);

    // After the CSS fade-out completes, actually switch the provider
    transitionTimeoutRef.current = window.setTimeout(() => {
      successHandledRef.current = false;
      currentProviderRef.current = provider;
      setCurrentProvider(provider);
      setStatus("loading");
      setHasPlayed(false);
      setProgress(0);
      setPlayerKey((prev) => prev + 1);
      setProviderStatuses((prev) =>
        prev.map((p) => ({
          ...p,
          status: p.provider === provider ? "testing" : p.status,
          error: p.provider === provider ? undefined : p.error,
        }))
      );
      // Fade back in immediately so the new player iframe is visible while loading
      setIsTransitioning(false);
    }, 300); // Match CSS transition duration
  };

  if (!browserInfo.isCompatible) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-800">
            Browser Compatibility Issue
          </h3>
        </div>
        <p className="text-yellow-700 mb-4">
          Your browser may not fully support video playback. Please try using a
          modern browser like:
        </p>
        <ul className="list-disc list-inside text-yellow-700 mb-4">
          <li>Google Chrome (latest version)</li>
          <li>Mozilla Firefox (latest version)</li>
          <li>Microsoft Edge (latest version)</li>
          <li>Safari (latest version)</li>
        </ul>
        <Button
          onClick={handleRetry}
          className="bg-yellow-600 hover:bg-yellow-700"
          size="sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  const renderVideoPlayer = () => {
    switch (currentProvider) {
      case "gumlet":
        return (
          <GumletPlayer
            videoID={videoIds.gumlet}
            autoplay={true}
            preload={true}
            muted={true}
            onReady={() => {
              console.log("Gumlet player is ready");
              // Fallback: if onPlay doesn't fire within 3s of ready,
              // treat as success (autoplay policy may block play event)
              setTimeout(() => {
                if (!successHandledRef.current) {
                  console.log("Gumlet onReady fallback → treating as success");
                  if (checkTimeoutRef.current) {
                    window.clearTimeout(checkTimeoutRef.current);
                  }
                  setHasPlayed(true);
                  handlePlaybackSuccess();
                }
              }, 3000);
            }}
            onPlay={() => {
              if (checkTimeoutRef.current) {
                window.clearTimeout(checkTimeoutRef.current);
              }
              setHasPlayed(true);
              handlePlaybackSuccess();
            }}
            onError={(e) => {
              console.error("Gumlet playback error:", e);
              handleProviderFailure();
            }}
            onProgress={(e) => {
              setProgress(e.percent);
            }}
            playerRef={undefined}
          />
        );
      case "bunnystream":
        return (
          <BunnyStreamPlayer
            videoId={videoIds.bunnystream}
            onPlay={() => {
              if (checkTimeoutRef.current) {
                window.clearTimeout(checkTimeoutRef.current);
              }
              setHasPlayed(true);
              handlePlaybackSuccess();
            }}
            onError={() => handleProviderFailure()}
            onProgress={(percent) => setProgress(percent)}
          />
        );
      case "vimeo":
        return (
          <VimeoPlayer
            videoId={videoIds.vimeo}
            onPlay={() => {
              if (checkTimeoutRef.current) {
                window.clearTimeout(checkTimeoutRef.current);
              }
              setHasPlayed(true);
              handlePlaybackSuccess();
            }}
            onError={() => handleProviderFailure()}
            onProgress={(percent) => setProgress(percent)}
            playerRef={undefined}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative overflow-hidden rounded-lg bg-black shadow-xl">
      {/* Indeterminate progress bar - visible during loading */}
      {status === "loading" && (
        <div className="absolute top-0 left-0 right-0 h-1 z-30 overflow-hidden bg-slate-700/50">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full"
            style={{
              width: "40%",
              animation: "progress-slide 1.4s ease-in-out infinite",
            }}
          />
        </div>
      )}

      {/* Player container with crossfade transition */}
      <div className="w-full aspect-video relative">
        {/* Gradient backdrop - always visible behind the player */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950" />

        {/* Transition screen between tests */}
        {nextProviderName && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mb-4" />
            <span className="text-base text-white font-medium capitalize">
              Loading {nextProviderName} player...
            </span>
          </div>
        )}

        {/* Player with crossfade */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-300 ease-in-out z-10",
            isTransitioning ? "opacity-0" : "opacity-100"
          )}
        >
          <div key={playerKey} className="w-full h-full">
            {renderVideoPlayer()}
          </div>
        </div>

        {/* Provider label */}
        <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/60 text-white text-xs font-medium capitalize pointer-events-none z-20">
          {currentProvider}
        </div>
      </div>

      {/* Status Overlay */}
      <div
        className={cn(
          "absolute top-4 right-4 px-3 py-2 rounded-full flex items-center transition-all duration-300 text-white",
          status === "success"
            ? "bg-green-600"
            : status === "failure"
              ? "bg-red-600"
              : "bg-slate-700/80"
        )}
      >
        {status === "loading" && (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            <span>Testing {currentProvider} playback...</span>
          </>
        )}
        {status === "success" && (
          <>
            <Check className="w-5 h-5 mr-2" />
            <span>Playback successful!</span>
          </>
        )}
        {status === "failure" && (
          <>
            <AlertTriangle className="w-5 h-5 mr-2" />
            <span>Playback issue detected</span>
          </>
        )}
      </div>

      {/* Provider Status Panel */}
      <div className="absolute top-4 left-4 z-30 bg-black/80 rounded-lg p-4 text-white max-w-xs">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center">
            <Info className="w-4 h-4 mr-2" />
            Provider Status
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-6 px-2 text-xs",
              isAutoTesting
                ? "text-red-400 hover:text-red-300"
                : "text-blue-400 hover:text-blue-300"
            )}
            onClick={() => (isAutoTesting ? stopAutoTest(false) : startAutoTest())}
          >
            {isAutoTesting ? "Stop Auto-Test" : "Start Auto-Test"}
          </Button>
        </div>
        <div className="space-y-2">
          {providerStatuses.map((providerStatus) => (
            <div
              key={providerStatus.provider}
              className="flex items-center justify-between text-sm"
            >
              <span className="capitalize">{providerStatus.provider}</span>
              <div className="flex items-center gap-2">
                {providerStatus.status === "pending" && (
                  <>
                    <span className="text-gray-400">{isAutoTesting ? "Queued" : "—"}</span>
                    {!isAutoTesting && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                        onClick={() =>
                          testSpecificProvider(providerStatus.provider)
                        }
                      >
                        Test
                      </Button>
                    )}
                  </>
                )}
                {providerStatus.status === "testing" && (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                )}
                {providerStatus.status === "success" && (
                  <>
                    <Check className="w-4 h-4 text-green-400" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                      onClick={() =>
                        testSpecificProvider(providerStatus.provider)
                      }
                      disabled={isAutoTesting}
                    >
                      Retest
                    </Button>
                  </>
                )}
                {providerStatus.status === "failure" && (
                  <>
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                      onClick={() =>
                        testSpecificProvider(providerStatus.provider)
                      }
                      disabled={isAutoTesting}
                    >
                      Retry
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        {providerStatuses.some((p) => p.error) && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <h4 className="text-xs font-semibold text-red-400 mb-1">Errors:</h4>
            {providerStatuses
              .filter((p) => p.error)
              .map((p) => (
                <div key={p.provider} className="text-xs text-red-300">
                  <span className="capitalize">{p.provider}:</span> {p.error}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {status === "success" && (
        <div className="absolute bottom-0 left-0 h-1 bg-primary/50 w-full">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Retry Button */}
      {status === "failure" && (
        <div className="absolute bottom-4 right-4">
          <Button
            onClick={handleRetry}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
